import { Button, IconButton } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';

export interface UnsavedChangesModalProps {
  readonly open: boolean;
  /** Confirma a saída, descartando as alterações não salvas. */
  readonly onConfirm: () => void;
  /** Cancela a saída e mantém o usuário na tela. */
  readonly onCancel: () => void;
}

/**
 * Modal de confirmação "Deseja sair sem salvar?" exibido quando o operador tenta
 * deixar o formulário de módulo com alterações não salvas. Controlado por `open`;
 * `onConfirm` prossegue com a saída e `onCancel` mantém a edição.
 */
export function UnsavedChangesModal({ open, onConfirm, onCancel }: UnsavedChangesModalProps) {
  const { t } = useTranslation('classModuleManagement');
  const { t: tCommon } = useTranslation('common');

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && onCancel()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />
        <Dialog.Content
          aria-describedby={undefined}
          className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 flex w-full max-w-[26rem] flex-col rounded-md bg-ctx-layout-surface shadow-level4 focus:outline-none"
        >
          <div className="flex items-center justify-between border-ctx-layout-border border-b-hairline px-xxs py-xxxs">
            <Dialog.Title className="text-ctx-content-title ts-heading-xs">
              {t('unsavedModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={tCommon('actions.cancel')}
              />
            </Dialog.Close>
          </div>

          <div className="px-xxs py-xxs">
            <p className="text-ctx-content-base ts-paragraph-xs">{t('unsavedModal.body')}</p>
          </div>

          <div className="flex justify-end gap-micro border-ctx-layout-border border-t-hairline px-xxs py-xxxs">
            <Button hierarchy="secondary" size="md" onClick={onCancel}>
              {t('unsavedModal.cancel')}
            </Button>
            <Button hierarchy="danger" size="md" onClick={onConfirm}>
              {t('unsavedModal.confirm')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
